<template>
  <div class="pl-2 pr-2">
    <div class="vessel-tracker">
      <!-- Terminal Filter Bar -->
      <b-card class="mb-2">
        <b-row align-v="center">
          <b-col cols="auto">
            <label class="mb-0 font-weight-bold">Filter by Terminal:</label>
          </b-col>
          <b-col cols="auto">
            <b-button-group size="sm">
              <b-button :variant="terminalFilter === 'All' ? 'secondary' : 'outline-secondary'" @click="setTerminalFilter('All')">All</b-button>
              <b-button id="btn-rb-mpt" :variant="terminalFilter === 'RB-MPT' ? 'secondary' : 'outline-secondary'" @click="setTerminalFilter('RB-MPT')">RB-MPT</b-button>
              <b-tooltip target="btn-rb-mpt" triggers="hover" placement="bottom">Richards Bay – MPT</b-tooltip>
              <b-button id="btn-rb-dbt" :variant="terminalFilter === 'RB-DBT' ? 'secondary' : 'outline-secondary'" @click="setTerminalFilter('RB-DBT')">RB-DBT</b-button>
              <b-tooltip target="btn-rb-dbt" triggers="hover" placement="bottom">Richards Bay – DBT</b-tooltip>
              <b-button id="btn-mw-mpt" :variant="terminalFilter === 'MW-MPT' ? 'secondary' : 'outline-secondary'" @click="setTerminalFilter('MW-MPT')">MW-MPT</b-button>
              <b-tooltip target="btn-mw-mpt" triggers="hover" placement="bottom">Maydon Wharf – MPT</b-tooltip>
              <b-button id="btn-bc-conv" :variant="terminalFilter === 'BC-Conveyer' ? 'secondary' : 'outline-secondary'" @click="setTerminalFilter('BC-Conveyer')">BC-Conv</b-button>
              <b-tooltip target="btn-bc-conv" triggers="hover" placement="bottom">Bulk Connections – Conveyer</b-tooltip>
              <b-button id="btn-bc-appl" :variant="terminalFilter === 'BC-Appliance' ? 'secondary' : 'outline-secondary'" @click="setTerminalFilter('BC-Appliance')">BC-Appl</b-button>
              <b-tooltip target="btn-bc-appl" triggers="hover" placement="bottom">Bulk Connections – Appliance</b-tooltip>
            </b-button-group>
          </b-col>
          <b-col>
            <small class="text-muted">Showing {{ filteredVessels.length }} of {{ allVessels.length }} vessels</small>
          </b-col>
        </b-row>
      </b-card>

      <!-- Tabs: Vessel Status | Parcel Data -->
      <b-card>
        <b-tabs content-class="mt-3" v-model="activeTab">
          <!-- === VESSEL STATUS TAB === -->
          <b-tab title="Vessel Status" active>
            <b-row class="mb-3">
              <b-col><h5>{{ vesselEditing ? 'Edit' : 'New' }} {{ vesselForm.terminal }} Vessel</h5></b-col>
            </b-row>
            <!-- Vessel Form -->
            <b-row>
              <b-col cols="3">
                <b-form-group label="Vessel Name *" description="Name of the vessel">
                  <div class="position-relative">
                    <b-form-input
                      v-model="vesselForm.vesselName"
                      :state="vesselForm.vesselName ? true : null"
                      placeholder="Type or select vessel name"
                      autocomplete="off"
                      @input="onVesselNameInput"
                      @focus="showVesselNameDropdown = true"
                      @blur="hideVesselNameDropdown"
                    />
                    <div v-if="showVesselNameDropdown && filteredVesselNames.length" class="vessel-name-dropdown">
                      <div
                        v-for="name in filteredVesselNames"
                        :key="name"
                        class="vessel-name-dropdown-item"
                        @mousedown.prevent="selectVesselName(name)"
                      >
                        {{ name }}
                      </div>
                    </div>
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="Voyage Number" description="Unique voyage identifier">
                  <b-form-input v-model="vesselForm.voyageNumber" placeholder="Enter voyage number" />
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="Total Shipment (MT)" description="Total metric tonnes for the shipment">
                  <b-form-input v-model="vesselForm.totalShipmentMT" type="number" step="0.01" placeholder="0.00" @keypress="numericOnly" />
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="Alongside LD Berth No." description="Berth number at the loading dock">
                  <b-form-input v-model="vesselForm.alongsideLDBerthNo" type="number" placeholder="Berth number" />
                </b-form-group>
              </b-col>
            </b-row>
            <!-- Date/Time fields (combined) -->
            <b-row>
              <b-col cols="3">
                <b-form-group label="Nomination Received" description="Date/time nomination was received">
                  <div class="d-flex align-items-center">
                    <b-form-input v-model="vesselForm.nominationReceivedDate" type="datetime-local" :state="dateState(vesselForm.nominationReceivedDate)" @change="autoFillTime(vesselForm, 'nominationReceivedDate')" class="flex-grow-1" ref="vNominationDate" />
                    <span class="date-clear-btn" @click="clearDate(vesselForm, 'nominationReceivedDate', 'vNominationDate')" title="Clear date">&times;</span>
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="ETA" description="Estimated Time of Arrival">
                  <div class="d-flex align-items-center">
                    <b-form-input v-model="vesselForm.etaDate" type="datetime-local" :state="dateState(vesselForm.etaDate)" @change="autoFillTime(vesselForm, 'etaDate')" class="flex-grow-1" ref="vEtaDate" />
                    <span class="date-clear-btn" @click="clearDate(vesselForm, 'etaDate', 'vEtaDate')" title="Clear date">&times;</span>
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="NOR" description="Notice of Readiness">
                  <div class="d-flex align-items-center">
                    <b-form-input v-model="vesselForm.norDate" type="datetime-local" :state="dateState(vesselForm.norDate)" @change="autoFillTime(vesselForm, 'norDate')" class="flex-grow-1" ref="vNorDate" />
                    <span class="date-clear-btn" @click="clearDate(vesselForm, 'norDate', 'vNorDate')" title="Clear date">&times;</span>
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="ETB" description="Estimated Time of Berthing">
                  <div class="d-flex align-items-center">
                    <b-form-input v-model="vesselForm.etbDate" type="datetime-local" :state="dateState(vesselForm.etbDate)" @change="autoFillTime(vesselForm, 'etbDate')" class="flex-grow-1" ref="vEtbDate" />
                    <span class="date-clear-btn" @click="clearDate(vesselForm, 'etbDate', 'vEtbDate')" title="Clear date">&times;</span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="3">
                <b-form-group label="Alongside LD Berth" description="Date/time vessel berthed at loading dock">
                  <div class="d-flex align-items-center">
                    <b-form-input v-model="vesselForm.alongsideLDBerthDate" type="datetime-local" :state="dateState(vesselForm.alongsideLDBerthDate)" @change="autoFillTime(vesselForm, 'alongsideLDBerthDate')" class="flex-grow-1" ref="vAlongsideDate" />
                    <span class="date-clear-btn" @click="clearDate(vesselForm, 'alongsideLDBerthDate', 'vAlongsideDate')" title="Clear date">&times;</span>
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="ETC" description="Estimated Time of Completion">
                  <div class="d-flex align-items-center">
                    <b-form-input v-model="vesselForm.etcDate" type="datetime-local" :state="dateState(vesselForm.etcDate)" @change="autoFillTime(vesselForm, 'etcDate')" class="flex-grow-1" ref="vEtcDate" />
                    <span class="date-clear-btn" @click="clearDate(vesselForm, 'etcDate', 'vEtcDate')" title="Clear date">&times;</span>
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="ETC/D - RCB" description="Completion / Departure - Ready for Customs & Border">
                  <div class="d-flex align-items-center">
                    <b-form-input v-model="vesselForm.etcdrcbDate" type="datetime-local" :state="dateState(vesselForm.etcdrcbDate)" @change="autoFillTime(vesselForm, 'etcdrcbDate')" class="flex-grow-1" ref="vEtcdrcbDate" />
                    <span class="date-clear-btn" @click="clearDate(vesselForm, 'etcdrcbDate', 'vEtcdrcbDate')" title="Clear date">&times;</span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group label="Remarks" description="General notes about this vessel call">
                  <b-form-input v-model="vesselForm.remarks" placeholder="Enter remarks" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="vesselEditing">
              <b-col cols="12">
                <b-form-group label="Comment (reason for edit)" description="Explain what was changed and why">
                  <b-form-input v-model="vesselForm.comment" placeholder="Describe what changed and why" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row class="mb-3">
              <b-col>
                <b-button variant="primary" @click="submitVessel" :disabled="!vesselForm.vesselName || vesselSaving || vesselHasInvalidDates">
                  <b-spinner small v-if="vesselSaving" class="mr-1"></b-spinner>
                  {{ vesselEditing ? 'Update Vessel' : 'Add Vessel' }}
                </b-button>
                <b-button variant="outline-secondary" class="ml-2" @click="resetVesselForm" v-if="vesselEditing">
                  Cancel Edit
                </b-button>
                <b-button variant="outline-danger" class="ml-2" @click="resetVesselForm" title="Clear all fields">
                  <b-icon-x-circle class="mr-1"></b-icon-x-circle> Clear
                </b-button>
              </b-col>
            </b-row>
            <!-- Last 10 Vessels Grid -->
            <b-row>
              <b-col>
                <h5>Recent Vessels
                  <b-badge variant="secondary">{{ recentVessels.length }}</b-badge>
                </h5>
                <b-table striped hover responsive :items="recentVessels" :fields="vesselFields"
                  :busy="vesselsLoading" show-empty empty-text="No vessels captured yet"
                  small class="vessel-table">
                  <template #table-busy>
                    <div class="text-center my-2"><b-spinner></b-spinner></div>
                  </template>
                  <template #cell(actions)="row">
                    <b-button size="sm" variant="outline-primary" @click="editVessel(row.item)" class="mr-1" title="Edit">
                      <b-icon-pencil></b-icon-pencil>
                    </b-button>
                    <b-button size="sm" variant="outline-info" @click="showAuditLog('Vessel', row.item.id, row.item.vesselName)" class="mr-1" title="Audit Log">
                      <b-icon-clock-history></b-icon-clock-history>
                    </b-button>
                    <b-button size="sm" variant="outline-danger" @click="deleteVessel(row.item.id)" title="Delete">
                      <b-icon-trash></b-icon-trash>
                    </b-button>
                  </template>
                  <template #cell(totalShipmentMT)="data">
                    {{ data.value ? Number(data.value).toLocaleString() : '' }}
                  </template>
                  <template #cell(nominationReceivedDate)="data">
                    {{ formatDate(data.value) }}
                  </template>
                  <template #cell(etaDate)="data">
                    {{ formatDate(data.value) }}
                  </template>
                  <template #cell(etbDate)="data">
                    {{ formatDate(data.value) }}
                  </template>
                  <template #cell(etcdrcbDate)="data">
                    {{ formatDate(data.value) }}
                  </template>
                </b-table>
              </b-col>
            </b-row>
          </b-tab>

          <!-- === PARCEL DATA TAB === -->
          <b-tab title="Parcel Data">
            <b-row class="mb-3">
              <b-col><h5>{{ parcelEditing ? 'Edit' : 'New' }} Parcel</h5></b-col>
            </b-row>
            <!-- Parcel Form -->
            <b-row>
              <b-col cols="3">
                <b-form-group label="Vessel Name *" description="Select the vessel this parcel belongs to">
                  <b-form-select v-model="parcelForm.selectedVesselName" :options="uniqueVesselNameOptions" @change="onVesselNameSelect">
                    <template #first><b-form-select-option :value="null" disabled>Select a vessel</b-form-select-option></template>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="Voyage Number *" description="Select the voyage for this parcel">
                  <b-form-select v-model="parcelForm.vesselStatusId" :options="voyageOptionsForSelectedVessel" :disabled="!parcelForm.selectedVesselName">
                    <template #first><b-form-select-option :value="null" disabled>Select a voyage</b-form-select-option></template>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="Parcel Name *" description="Cargo parcel identifier">
                  <b-form-input v-model="parcelForm.parcelName" placeholder="Enter parcel name" />
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="Parcel Shipment Tons (MT)" description="Total metric tonnes for this parcel">
                  <b-form-input v-model="parcelForm.parcelShipmentTonsMT" type="number" step="0.01" placeholder="0.00" @keypress="numericOnly" @input="calcBalToGo" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="3">
                <b-form-group label="Destination" description="Port or location the parcel is destined for">
                  <b-form-input v-model="parcelForm.destination" placeholder="Enter destination" />
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="Commenced Date/Time" description="Date/time loading commenced">
                  <div class="d-flex align-items-center">
                    <b-form-input v-model="parcelForm.commencedDate" type="datetime-local" :state="dateState(parcelForm.commencedDate)" @change="autoFillTime(parcelForm, 'commencedDate')" class="flex-grow-1" ref="pCommencedDate" />
                    <span class="date-clear-btn" @click="clearDate(parcelForm, 'commencedDate', 'pCommencedDate')" title="Clear date">&times;</span>
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="Completed Date/Time" description="Date/time loading completed">
                  <div class="d-flex align-items-center">
                    <b-form-input v-model="parcelForm.completedDate" type="datetime-local" :state="dateState(parcelForm.completedDate)" @change="autoFillTime(parcelForm, 'completedDate')" class="flex-grow-1" ref="pCompletedDate" />
                    <span class="date-clear-btn" @click="clearDate(parcelForm, 'completedDate', 'pCompletedDate')" title="Clear date">&times;</span>
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group label="Loaded (MT)" description="Actual metric tonnes loaded">
                  <b-form-input v-model="parcelForm.loadedMT" type="number" step="0.01" placeholder="0.00" @keypress="numericOnly" @input="calcBalToGo" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="2">
                <b-form-group label="Bal To Go (MT)" description="Remaining tonnes (auto-calculated)">
                  <b-form-input v-model="parcelForm.balToGoMT" type="number" step="0.01" disabled />
                </b-form-group>
              </b-col>
              <b-col cols="2">
                <b-form-group label="Stowage" description="Hold/hatch stowage plan">
                  <b-form-input v-model="parcelForm.stowage" placeholder="e.g. #3 DROP" />
                </b-form-group>
              </b-col>
              <b-col cols="8">
                <b-form-group label="Remarks" description="Additional notes about this parcel">
                  <b-form-input v-model="parcelForm.remarks" placeholder="Enter remarks" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="parcelEditing">
              <b-col cols="12">
                <b-form-group label="Comment (reason for edit)" description="Explain what was changed and why">
                  <b-form-input v-model="parcelForm.comment" placeholder="Describe what changed and why" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row class="mb-3">
              <b-col>
                <b-button variant="primary" @click="submitParcel"
                  :disabled="!parcelForm.vesselStatusId || !parcelForm.parcelName || parcelSaving || parcelHasInvalidDates">
                  <b-spinner small v-if="parcelSaving" class="mr-1"></b-spinner>
                  {{ parcelEditing ? 'Update Parcel' : 'Add Parcel' }}
                </b-button>
                <b-button variant="outline-secondary" class="ml-2" @click="resetParcelForm" v-if="parcelEditing">
                  Cancel Edit
                </b-button>
                <b-button variant="outline-danger" class="ml-2" @click="resetParcelForm" title="Clear all fields">
                  <b-icon-x-circle class="mr-1"></b-icon-x-circle> Clear
                </b-button>
              </b-col>
            </b-row>
            <!-- Last 10 Parcels Grid -->
            <b-row>
              <b-col>
                <h5>Recent Parcels
                  <b-badge variant="secondary">{{ recentParcels.length }}</b-badge>
                </h5>
                <b-table striped hover responsive :items="recentParcels" :fields="parcelFields"
                  :busy="parcelsLoading" show-empty empty-text="No parcels captured yet"
                  small class="parcel-table">
                  <template #table-busy>
                    <div class="text-center my-2"><b-spinner></b-spinner></div>
                  </template>
                  <template #cell(actions)="row">
                    <b-button size="sm" variant="outline-primary" @click="editParcel(row.item)" class="mr-1" title="Edit">
                      <b-icon-pencil></b-icon-pencil>
                    </b-button>
                    <b-button size="sm" variant="outline-info" @click="showAuditLog('Parcel', row.item.id, row.item.parcelName)" class="mr-1" title="Audit Log">
                      <b-icon-clock-history></b-icon-clock-history>
                    </b-button>
                    <b-button size="sm" variant="outline-danger" @click="deleteParcel(row.item.id)" title="Delete">
                      <b-icon-trash></b-icon-trash>
                    </b-button>
                  </template>
                  <template #cell(parcelShipmentTonsMT)="data">
                    {{ data.value ? Number(data.value).toLocaleString() : '' }}
                  </template>
                  <template #cell(loadedMT)="data">
                    {{ data.value ? Number(data.value).toLocaleString() : '' }}
                  </template>
                  <template #cell(commencedDate)="data">
                    {{ formatDateTime(data.value) }}
                  </template>
                  <template #cell(completedDate)="data">
                    {{ formatDateTime(data.value) }}
                  </template>
                </b-table>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>

    <!-- Charts Section (below tables) -->
    <b-card class="mb-2 mt-2">
      <b-row>
        <b-col><h4>{{ terminalFilter === 'All' ? 'All' : terminalFilter }} Vessel Performance Charts</h4></b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <h6 class="text-center">Vessel Turnaround (Days)</h6>
          <div class="chart-container" style="height: 260px;">
            <Bar v-if="vesselTurnChart.data.labels.length"
              :chart-options="vesselTurnChart.options" :chart-data="vesselTurnChart.data"
              chart-id="vesselTurnChart" :key="'vturn-' + chartKey" />
            <p v-else class="text-center text-muted pt-4">No vessel turnaround data — fill in NOR, Alongside LD Berth, ETB and ETC/D-RCB dates</p>
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col cols="6">
          <h6 class="text-center">Parcel Loading Duration (Hours)</h6>
          <div class="chart-container" style="height: 240px;">
            <Bar v-if="durationChart.data.labels.length"
              :chart-options="durationChart.options" :chart-data="durationChart.data"
              chart-id="durationChart" :key="'dur-' + chartKey" />
            <p v-else class="text-center text-muted pt-4">No parcel data — add parcels with Commenced, Completed and Loaded MT</p>
          </div>
        </b-col>
        <b-col cols="6">
          <h6 class="text-center">Achieved Norm vs Fixed Norm (MT/Hour)</h6>
          <div class="chart-container" style="height: 240px;">
            <Bar v-if="normChart.data.labels.length"
              :chart-options="normChart.options" :chart-data="normChart.data"
              chart-id="normChart" :key="'norm-' + chartKey" />
            <p v-else class="text-center text-muted pt-4">No parcel data — add parcels with Commenced, Completed and Loaded MT</p>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Audit Log Modal -->
    <b-modal v-model="auditModal.show" :title="'Audit Log: ' + auditModal.label" size="xl" ok-only ok-title="Close" scrollable>
      <div v-if="auditModal.loading" class="text-center py-3"><b-spinner></b-spinner></div>
      <div v-else-if="!auditModal.entries.length" class="text-center text-muted py-3">No audit logs recorded yet.</div>
      <div v-else>
        <div v-for="entry in auditModal.entries" :key="entry.id" class="mb-3 border-bottom pb-2">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div>
              <b-badge :variant="entry.changeType === 'Created' ? 'success' : entry.changeType === 'Deleted' ? 'danger' : 'warning'">
                {{ entry.changeType }}
              </b-badge>
              <small class="ml-2 text-muted">{{ formatDateTime(entry.changeDate) }}</small>
            </div>
            <small v-if="entry.comment" class="text-info"><strong>Comment:</strong> {{ entry.comment }}</small>
          </div>
          <!-- Vessel audit snapshot -->
          <b-table-simple v-if="auditModal.entityType === 'Vessel'" small bordered responsive class="mb-0 audit-snapshot-table">
            <b-thead><b-tr>
              <b-th>Vessel Name</b-th><b-th>Voyage No.</b-th><b-th>Terminal</b-th><b-th>Shipment MT</b-th>
              <b-th>Nom. Received</b-th><b-th>ETA</b-th><b-th>NOR</b-th>
              <b-th>ETB</b-th><b-th>Berth No.</b-th>
              <b-th>Alongside Berth</b-th><b-th>ETC</b-th>
              <b-th>ETC/D-RCB</b-th><b-th>Remarks</b-th><b-th>Days in Port</b-th>
            </b-tr></b-thead>
            <b-tbody><b-tr>
              <b-td :class="isChanged(entry, 'VesselName')">{{ entry.vesselName || '' }}</b-td>
              <b-td :class="isChanged(entry, 'VoyageNumber')">{{ entry.voyageNumber || '' }}</b-td>
              <b-td :class="isChanged(entry, 'Terminal')">{{ entry.terminal || '' }}</b-td>
              <b-td :class="isChanged(entry, 'TotalShipmentMT')">{{ entry.totalShipmentMT != null ? Number(entry.totalShipmentMT).toLocaleString() : '' }}</b-td>
              <b-td :class="isChanged(entry, 'NominationReceivedDate')">{{ formatDateTime(entry.nominationReceivedDate) }}</b-td>
              <b-td :class="isChanged(entry, 'ETADate')">{{ formatDateTime(entry.etaDate) }}</b-td>
              <b-td :class="isChanged(entry, 'NORDate')">{{ formatDateTime(entry.norDate) }}</b-td>
              <b-td :class="isChanged(entry, 'ETBDate')">{{ formatDateTime(entry.etbDate) }}</b-td>
              <b-td :class="isChanged(entry, 'AlongsideLDBerthNo')">{{ entry.alongsideLDBerthNo || '' }}</b-td>
              <b-td :class="isChanged(entry, 'AlongsideLDBerthDate')">{{ formatDateTime(entry.alongsideLDBerthDate) }}</b-td>
              <b-td :class="isChanged(entry, 'ETCDate')">{{ formatDateTime(entry.etcDate) }}</b-td>
              <b-td :class="isChanged(entry, 'ETCDRCBDate')">{{ formatDateTime(entry.etcdrcbDate) }}</b-td>
              <b-td :class="isChanged(entry, 'Remarks')">{{ entry.remarks || '' }}</b-td>
              <b-td :class="isChanged(entry, 'DaysInPort')">{{ entry.daysInPort || '' }}</b-td>
            </b-tr></b-tbody>
          </b-table-simple>
          <!-- Parcel audit snapshot -->
          <b-table-simple v-if="auditModal.entityType === 'Parcel'" small bordered responsive class="mb-0 audit-snapshot-table">
            <b-thead><b-tr>
              <b-th>Vessel</b-th><b-th>Voyage No.</b-th><b-th>Parcel Name</b-th><b-th>Shipment MT</b-th>
              <b-th>Destination</b-th><b-th>Commenced</b-th><b-th>Completed</b-th>
              <b-th>Loaded MT</b-th><b-th>Bal To Go</b-th><b-th>Stowage</b-th><b-th>Remarks</b-th>
            </b-tr></b-thead>
            <b-tbody><b-tr>
              <b-td :class="isChanged(entry, 'VesselName')">{{ entry.vesselName || '' }}</b-td>
              <b-td :class="isChanged(entry, 'VoyageNumber')">{{ entry.voyageNumber || '' }}</b-td>
              <b-td :class="isChanged(entry, 'ParcelName')">{{ entry.parcelName || '' }}</b-td>
              <b-td :class="isChanged(entry, 'ParcelShipmentTonsMT')">{{ entry.parcelShipmentTonsMT != null ? Number(entry.parcelShipmentTonsMT).toLocaleString() : '' }}</b-td>
              <b-td :class="isChanged(entry, 'Destination')">{{ entry.destination || '' }}</b-td>
              <b-td :class="isChanged(entry, 'CommencedDate')">{{ formatDateTime(entry.commencedDate) }}</b-td>
              <b-td :class="isChanged(entry, 'CompletedDate')">{{ formatDateTime(entry.completedDate) }}</b-td>
              <b-td :class="isChanged(entry, 'LoadedMT')">{{ entry.loadedMT != null ? Number(entry.loadedMT).toLocaleString() : '' }}</b-td>
              <b-td :class="isChanged(entry, 'BalToGoMT')">{{ entry.balToGoMT != null ? Number(entry.balToGoMT).toLocaleString() : '' }}</b-td>
              <b-td :class="isChanged(entry, 'Stowage')">{{ entry.stowage || '' }}</b-td>
              <b-td :class="isChanged(entry, 'Remarks')">{{ entry.remarks || '' }}</b-td>
            </b-tr></b-tbody>
          </b-table-simple>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs/legacy'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import api from '../api'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

export default {
  name: 'VesselTracker',
  components: { Bar },
  data() {
    return {
      activeTab: 0,
      chartKey: 0,
      terminalFilter: 'All',

      // Vessel form
      vesselForm: this.emptyVesselForm(),
      vesselEditing: false,
      vesselSaving: false,
      showVesselNameDropdown: false,

      // Parcel form
      parcelForm: this.emptyParcelForm(),
      parcelEditing: false,
      parcelSaving: false,

      // Lists
      allVessels: [],
      allParcels: [],
      vesselsLoading: false,
      parcelsLoading: false,

      // Audit log modal
      auditModal: {
        show: false,
        loading: false,
        label: '',
        entityType: '',
        entries: []
      },

      // Table fields
      vesselFields: [
        { key: 'id', label: '#', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'vesselName', label: 'Vessel Name', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'voyageNumber', label: 'Voyage No.', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'terminal', label: 'Terminal', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'totalShipmentMT', label: 'Shipment (MT)', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'nominationReceivedDate', label: 'Nom. Received', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'etaDate', label: 'ETA', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'etbDate', label: 'ETB', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'etcdrcbDate', label: 'ETC/D-RCB', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'daysInPort', label: 'Days in Port', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'remarks', label: 'Remarks', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'actions', label: '', class: 'text-nowrap', thClass: 'text-center', tdClass: 'text-center' }
      ],
      parcelFields: [
        { key: 'id', label: '#', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'vesselName', label: 'Vessel', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'voyageNumber', label: 'Voyage No.', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'parcelName', label: 'Parcel', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'parcelShipmentTonsMT', label: 'Shipment (MT)', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'destination', label: 'Destination', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'commencedDate', label: 'Commenced', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'completedDate', label: 'Completed', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'loadedMT', label: 'Loaded (MT)', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'balToGoMT', label: 'Bal To Go', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'stowage', label: 'Stowage', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'remarks', label: 'Remarks', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'actions', label: '', class: 'text-nowrap', thClass: 'text-center', tdClass: 'text-center' }
      ],

      // Charts
      durationChart: {
        data: { labels: [], datasets: [] },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true, title: { display: true, text: 'Hours' } },
            x: { ticks: { maxRotation: 45, minRotation: 45 } }
          }
        }
      },
      normChart: {
        data: { labels: [], datasets: [] },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: true, position: 'top' } },
          scales: {
            y: { beginAtZero: true, title: { display: true, text: 'MT/Hour' } },
            x: { ticks: { maxRotation: 45, minRotation: 45 } }
          }
        }
      },
      vesselTurnChart: {
        data: { labels: [], datasets: [] },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: true, position: 'top' } },
          scales: {
            y: { beginAtZero: true, title: { display: true, text: 'Days' } },
            x: { ticks: { maxRotation: 45, minRotation: 45 } }
          }
        }
      }
    }
  },
  computed: {
    filteredVessels() {
      if (this.terminalFilter === 'All') return this.allVessels
      return this.allVessels.filter(v => v.terminal === this.terminalFilter)
    },
    recentVessels() {
      return this.filteredVessels.slice(0, 10)
    },
    recentParcels() {
      if (this.terminalFilter === 'All') return this.allParcels.slice(0, 10)
      const vesselIds = new Set(this.filteredVessels.map(v => v.id))
      return this.allParcels.filter(p => vesselIds.has(p.vesselStatusId)).slice(0, 10)
    },
    vesselOptions() {
      return this.allVessels.map(v => ({ value: v.id, text: v.vesselName || ('Vessel #' + v.id) }))
    },
    uniqueVesselNameOptions() {
      // Unique vessel names, filtered by terminal if applicable
      const source = this.terminalFilter === 'All' ? this.allVessels : this.filteredVessels
      const seen = new Set()
      return source
        .filter(v => {
          const name = v.vesselName || ''
          if (!name || seen.has(name)) return false
          seen.add(name)
          return true
        })
        .map(v => ({ value: v.vesselName, text: v.vesselName }))
    },
    voyageOptionsForSelectedVessel() {
      if (!this.parcelForm.selectedVesselName) return []
      const source = this.terminalFilter === 'All' ? this.allVessels : this.filteredVessels
      return source
        .filter(v => v.vesselName === this.parcelForm.selectedVesselName)
        .map(v => ({ value: v.id, text: v.voyageNumber || ('Trip #' + v.id) }))
        .reverse() // latest first (highest id first)
    },
    filteredVesselNames() {
      const query = (this.vesselForm.vesselName || '').toLowerCase()
      if (!query) {
        return [...new Set(this.allVessels.map(v => v.vesselName).filter(Boolean))]
      }
      const names = [...new Set(this.allVessels.map(v => v.vesselName).filter(Boolean))]
      return names.filter(n => n.toLowerCase().includes(query) && n.toLowerCase() !== query)
    },
    vesselHasInvalidDates() {
      const dateFields = ['nominationReceivedDate','etaDate','norDate','etbDate',
        'alongsideLDBerthDate','etcDate','etcdrcbDate']
      return dateFields.some(f => this.dateState(this.vesselForm[f]) === false)
    },
    parcelHasInvalidDates() {
      return this.dateState(this.parcelForm.commencedDate) === false ||
             this.dateState(this.parcelForm.completedDate) === false
    }
  },
  mounted() {
    this.loadVessels()
    this.loadRecentParcels()
  },
  methods: {
    setTerminalFilter(val) {
      this.terminalFilter = val
      this.vesselForm.terminal = val === 'All' ? 'RB-MPT' : val
      this.loadChartData()
    },
    emptyVesselForm() {
      const t = this.terminalFilter && this.terminalFilter !== 'All' ? this.terminalFilter : 'RB-MPT'
      return {
        id: null, vesselName: '', voyageNumber: '', terminal: t, totalShipmentMT: null, alongsideLDBerthNo: null,
        nominationReceivedDate: '',
        etaDate: '',
        norDate: '',
        etbDate: '',
        alongsideLDBerthDate: '',
        etcDate: '',
        etcdrcbDate: '',
        remarks: '',
        comment: ''
      }
    },
    emptyParcelForm() {
      return {
        id: null, vesselStatusId: null, selectedVesselName: null, parcelName: '', parcelShipmentTonsMT: null,
        destination: '', commencedDate: '', completedDate: '',
        loadedMT: null, balToGoMT: null, stowage: '', remarks: '', comment: ''
      }
    },
    resetVesselForm() {
      this.vesselForm = this.emptyVesselForm()
      this.vesselEditing = false
    },
    resetParcelForm() {
      this.parcelForm = this.emptyParcelForm()
      this.parcelEditing = false
    },

    // === Vessel Name Combobox ===
    onVesselNameInput() {
      this.showVesselNameDropdown = true
    },
    selectVesselName(name) {
      this.vesselForm.vesselName = name
      this.showVesselNameDropdown = false
    },
    hideVesselNameDropdown() {
      // Small delay so mousedown on dropdown item fires first
      setTimeout(() => { this.showVesselNameDropdown = false }, 150)
    },

    // === Date Helpers ===
    numericOnly(e) {
      const char = String.fromCharCode(e.which || e.keyCode)
      if (!/[\d.]/.test(char) && e.key !== 'Backspace' && e.key !== 'Tab') {
        e.preventDefault()
      }
    },
    autoFillTime(form, field) {
      const val = form[field]
      if (!val) return
      // If value is date-only (no T or time is 00:00 from browser default), fill current time
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
        const now = new Date()
        const hh = String(now.getHours()).padStart(2, '0')
        const mm = String(now.getMinutes()).padStart(2, '0')
        form[field] = val + 'T' + hh + ':' + mm
      } else if (/^\d{4}-\d{2}-\d{2}T00:00/.test(val)) {
        const now = new Date()
        const hh = String(now.getHours()).padStart(2, '0')
        const mm = String(now.getMinutes()).padStart(2, '0')
        form[field] = val.substring(0, 11) + hh + ':' + mm
      }
    },
    clearDate(form, field, refName) {
      form[field] = ''
      // Also clear the native DOM input to handle partial/incomplete values
      const ref = this.$refs[refName]
      if (ref) {
        const el = ref.$el || ref
        if (el && el.tagName === 'INPUT') el.value = ''
        else { const inp = el.querySelector && el.querySelector('input'); if (inp) inp.value = '' }
      }
    },
    dateState(val) {
      // null/undefined/empty = not touched, no validation shown
      if (val === null || val === undefined || val === '') return null
      // valid datetime-local value has format YYYY-MM-DDTHH:mm
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(val) ? true : false
    },
    sanitizeDates(obj, fields) {
      for (const f of fields) {
        if (obj[f] !== null && obj[f] !== undefined && !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(obj[f])) {
          obj[f] = null
        }
      }
    },
    toLocalInput(val) {
      if (!val) return null
      // datetime-local expects 'YYYY-MM-DDTHH:mm'
      return val.substring(0, 16)
    },
    formatDate(val) {
      if (!val) return ''
      try { return new Date(val).toLocaleDateString() } catch { return val }
    },
    formatDateTime(val) {
      if (!val) return ''
      try { return new Date(val).toLocaleString() } catch { return val }
    },

    // === Vessels ===
    loadVessels() {
      this.vesselsLoading = true
      api.GetAllVessels(
        (data) => {
          this.allVessels = data
          this.vesselsLoading = false
          this.loadChartData()
        },
        (err) => { console.error(err); this.vesselsLoading = false }
      )
    },
    submitVessel() {
      this.vesselSaving = true
      const payload = { ...this.vesselForm }
      if (!this.vesselEditing) delete payload.id
      this.sanitizeDates(payload, ['nominationReceivedDate','etaDate','norDate','etbDate',
        'alongsideLDBerthDate','etcDate','etcdrcbDate'])
      const callback = () => {
        this.vesselSaving = false
        this.resetVesselForm()
        this.loadVessels()
        this.loadChartData()
      }
      const errCb = (err) => { console.error(err); this.vesselSaving = false }

      if (this.vesselEditing) {
        api.UpdateVessel(payload, callback, errCb)
      } else {
        api.CreateVessel(payload, callback, errCb)
      }
    },
    editVessel(item) {
      this.vesselEditing = true
      this.vesselForm = {
        id: item.id,
        vesselName: item.vesselName,
        voyageNumber: item.voyageNumber || '',
        terminal: item.terminal || 'RB-MPT',
        totalShipmentMT: item.totalShipmentMT,
        alongsideLDBerthNo: item.alongsideLDBerthNo,
        nominationReceivedDate: this.toLocalInput(item.nominationReceivedDate),
        etaDate: this.toLocalInput(item.etaDate),
        norDate: this.toLocalInput(item.norDate),
        etbDate: this.toLocalInput(item.etbDate),
        alongsideLDBerthDate: this.toLocalInput(item.alongsideLDBerthDate),
        etcDate: this.toLocalInput(item.etcDate),
        etcdrcbDate: this.toLocalInput(item.etcdrcbDate),
        remarks: item.remarks || '',
        comment: ''
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    deleteVessel(id) {
      if (!confirm('Delete this vessel and all its parcels?')) return
      api.DeleteVessel(id,
        () => { this.loadVessels(); this.loadRecentParcels(); this.loadChartData() },
        (err) => console.error(err)
      )
    },

    // === Parcels ===
    loadRecentParcels() {
      this.parcelsLoading = true
      api.GetRecentParcels(100,
        (data) => { this.allParcels = data; this.parcelsLoading = false; this.loadChartData() },
        (err) => { console.error(err); this.parcelsLoading = false }
      )
    },
    onVesselNameSelect() {
      // When vessel name changes, auto-select the latest voyage
      const opts = this.voyageOptionsForSelectedVessel
      this.parcelForm.vesselStatusId = opts.length ? opts[0].value : null
    },
    calcBalToGo() {
      const shipment = parseFloat(this.parcelForm.parcelShipmentTonsMT) || 0
      const loaded = parseFloat(this.parcelForm.loadedMT) || 0
      this.parcelForm.balToGoMT = Math.max(0, shipment - loaded)
    },
    submitParcel() {
      this.parcelSaving = true
      const payload = { ...this.parcelForm }
      delete payload.selectedVesselName
      if (!this.parcelEditing) delete payload.id
      this.sanitizeDates(payload, ['commencedDate','completedDate'])
      const callback = () => {
        this.parcelSaving = false
        this.resetParcelForm()
        this.loadRecentParcels()
        this.loadChartData()
      }
      const errCb = (err) => { console.error(err); this.parcelSaving = false }

      if (this.parcelEditing) {
        api.UpdateParcel(payload, callback, errCb)
      } else {
        api.CreateParcel(payload, callback, errCb)
      }
    },
    editParcel(item) {
      this.parcelEditing = true
      this.parcelForm = {
        id: item.id,
        vesselStatusId: item.vesselStatusId,
        selectedVesselName: item.vesselName || null,
        parcelName: item.parcelName,
        parcelShipmentTonsMT: item.parcelShipmentTonsMT,
        destination: item.destination,
        commencedDate: item.commencedDate ? item.commencedDate.substring(0, 16) : null,
        completedDate: item.completedDate ? item.completedDate.substring(0, 16) : null,
        loadedMT: item.loadedMT,
        balToGoMT: item.balToGoMT,
        stowage: item.stowage,
        remarks: item.remarks || '',
        comment: ''
      }
      this.activeTab = 1
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    deleteParcel(id) {
      if (!confirm('Delete this parcel?')) return
      api.DeleteParcel(id,
        () => { this.loadRecentParcels(); this.loadChartData() },
        (err) => console.error(err)
      )
    },

    // === Audit Log ===
    showAuditLog(entityType, entityId, label) {
      this.auditModal.show = true
      this.auditModal.loading = true
      this.auditModal.label = (label || entityType + ' #' + entityId)
      this.auditModal.entityType = entityType
      this.auditModal.entries = []
      const apiFn = entityType === 'Vessel' ? api.GetVesselAuditLogs : api.GetParcelAuditLogs
      apiFn.call(api, entityId,
        (data) => { this.auditModal.entries = data; this.auditModal.loading = false },
        (err) => { console.error(err); this.auditModal.loading = false }
      )
    },
    isChanged(entry, fieldName) {
      if (entry.changeType !== 'Updated') return ''
      try {
        const changed = JSON.parse(entry.changedFields || '[]')
        return changed.includes(fieldName) ? 'audit-changed' : ''
      } catch { return '' }
    },

    // === Charts ===
    loadChartData() {
      // Use the exact same vessel names shown in the table (max 10)
      const recentVesselNames = this.recentVessels.map(v => v.vesselName).filter(Boolean)
      // Use the exact same parcel names shown in the table (max 10)
      const recentParcelNames = this.recentParcels.map(p => p.parcelName).filter(Boolean)

      api.GetParcelChartData(
        (data) => {
          const filtered = data.filter(d => recentParcelNames.includes(d.parcelName)).slice(0, 10)
          // Duration chart
          this.durationChart.data = {
            labels: filtered.map(d => d.parcelName),
            datasets: [{
              label: 'Loading Duration (Hours)',
              backgroundColor: '#4e79a7',
              data: filtered.map(d => d.loadingDurationHours)
            }]
          }
          // Norm chart
          this.normChart.data = {
            labels: filtered.map(d => d.parcelName),
            datasets: [
              {
                label: 'Achieved Norm (MT/Hr)',
                backgroundColor: '#59a14f',
                data: filtered.map(d => d.achievedNormMTPerHour)
              },
              {
                label: 'Fixed Norm DBT (850)',
                backgroundColor: '#e15759',
                data: filtered.map(d => d.fixedNormDBT)
              },
              {
                label: 'Fixed Norm Chrome Ore (850)',
                backgroundColor: '#f28e2b',
                data: filtered.map(d => d.fixedNormChromeOre)
              }
            ]
          }
          this.chartKey++
        },
        (err) => console.error(err)
      )
      api.GetVesselChartData(
        (data) => {
          const filtered = data.filter(d => recentVesselNames.includes(d.vesselName)).slice(0, 10)
          const hasData = filtered.some(d => d.norToAlongsideDays != null || d.portCallDays != null || d.daysInPort != null)
          if (!hasData) {
            this.vesselTurnChart.data = { labels: [], datasets: [] }
            this.chartKey++
            return
          }

          // Collect all non-null values and determine a sensible Y-axis cap
          const allVals = filtered.flatMap(d => [d.norToAlongsideDays, d.portCallDays, d.daysInPort]).filter(v => v != null)
          const sorted = [...allVals].sort((a, b) => a - b)
          const p90 = sorted[Math.floor(sorted.length * 0.9)] || 0
          const median = sorted[Math.floor(sorted.length * 0.5)] || 0
          // Cap at 3x the median, or 1.5x the 90th percentile (whichever is larger), minimum 30
          const yCap = Math.max(Math.ceil(Math.max(median * 3, p90 * 1.5)), 30)
          const hasOutliers = allVals.some(v => v > yCap)

          // Clamp display values but keep originals for labels
          const rawNor = filtered.map(d => d.norToAlongsideDays)
          const rawPort = filtered.map(d => d.portCallDays)
          const rawDays = filtered.map(d => d.daysInPort)
          const clamp = v => v != null && v > yCap ? yCap : v

          this.vesselTurnChart.data = {
            labels: filtered.map(d => d.vesselName || 'Unknown'),
            datasets: [
              {
                label: 'NOR to Alongside (Days)',
                backgroundColor: '#4e79a7',
                data: rawNor.map(clamp),
                _raw: rawNor
              },
              {
                label: 'Days in Port',
                backgroundColor: '#f28e2b',
                data: rawDays.map(clamp),
                _raw: rawDays
              },
              {
                label: 'Port Call (Days)',
                backgroundColor: '#59a14f',
                data: rawPort.map(clamp),
                _raw: rawPort
              }
            ]
          }

          // Update Y-axis cap and show real values on top of bars
          this.vesselTurnChart.options = {
            ...this.vesselTurnChart.options,
            scales: {
              ...this.vesselTurnChart.options.scales,
              y: {
                beginAtZero: true,
                title: { display: true, text: 'Days' },
                ...(hasOutliers ? { max: yCap } : {})
              }
            },
            plugins: {
              ...this.vesselTurnChart.options.plugins,
              tooltip: {
                callbacks: {
                  label: function (ctx) {
                    const raw = ctx.dataset._raw ? ctx.dataset._raw[ctx.dataIndex] : ctx.raw
                    const val = raw != null ? raw : 'N/A'
                    return ctx.dataset.label + ': ' + val
                  }
                }
              },
              datalabels: false
            }
          }

          this.chartKey++
        },
        (err) => console.error(err)
      )
    }
  }
}
</script>

<style scoped>
.vessel-tracker h4 {
  margin-bottom: 0;
}
.vessel-table, .parcel-table {
  font-size: 0.85rem;
}
.vessel-name-dropdown {
  position: absolute;
  z-index: 1050;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ced4da;
  border-top: 0;
  border-radius: 0 0 .25rem .25rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, .1);
}
.vessel-name-dropdown-item {
  padding: .4rem .75rem;
  cursor: pointer;
  font-size: 0.9rem;
}
.vessel-name-dropdown-item:hover {
  background-color: #e9ecef;
}
.chart-container {
  position: relative;
  overflow: hidden;
  width: 100%;
}
.audit-changed {
  background-color: #fff3cd !important;
  font-weight: bold;
}
.audit-snapshot-table {
  font-size: 0.8rem;
}
.date-clear-btn {
  margin-left: 6px;
  cursor: pointer;
  color: #dc3545;
  font-size: 1.4rem;
  font-weight: bold;
  line-height: 1;
  user-select: none;
  min-width: 20px;
  text-align: center;
}
.date-clear-btn:hover {
  color: #a71d2a;
}
</style>
